import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputBase,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { get } from "../../helpers/aws_api_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import * as url from "../../helpers/url_helper";
import { summaryTheme as t } from "./styles";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const DETAIL_TABLE_COLUMNS = [
  { id: "arn", label: "Resource (ARN)", desc: "Amazon Resource Name — unique identifier for the resource", width: "20%", minWidth: 180 },
  { id: "service", label: "Service", desc: "AWS service or product (e.g. S3, IAM, EC2)", width: "8%" },
  { id: "region", label: "Region", desc: "AWS region where the resource is located", width: "8%" },
  { id: "modules", label: "Modules", desc: "CloudCatcher modules or scanners that evaluated this resource", width: "10%" },
  { id: "security_failed_checks", label: "Security", desc: "Security status and count of failed checks by severity (CRITICAL, HIGH, MEDIUM, LOW)", width: "18%" },
  { id: "compliance_failed_total", label: "Compliance", desc: "Compliance status and failed controls; hover for control breakdown", width: "16%" },
  { id: "cost_unblended_mtd", label: "Cost MTD", desc: "Unblended cost for this resource month-to-date (USD)", width: "8%" },
  { id: "snapshot_date", label: "Snapshot", desc: "Date of the last data snapshot used for this view", width: "8%" },
];

function formatCost(value) {
  if (value == null || (typeof value !== "number" && typeof value !== "string")) return "—";
  const num = typeof value === "string" ? parseFloat(value, 10) : value;
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(dateString) {
  if (!dateString) return "—";
  try {
    const parts = String(dateString).split("-");
    if (parts.length >= 3) {
      const y = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      const d = parseInt(parts[2], 10);
      if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
        return new Date(y, m, d).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    }
    return String(dateString);
  } catch {
    return String(dateString);
  }
}

/** Shadcn-style tooltip: header + list (shown on hover) */
const complianceTooltipSx = {
  fontFamily: t.fontFamily,
  maxWidth: 320,
  padding: 0,
  borderRadius: t.radius.lg,
  border: `1px solid ${t.colors.border}`,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.02)",
  backgroundColor: t.colors.card,
  overflow: "hidden",
};

/** Compliance cell: only the status chip; hover on chip shows custom tooltip with failed controls list */
function ComplianceCell({ row }) {
  const controls = row.compliance_failed_controls && typeof row.compliance_failed_controls === "object" ? row.compliance_failed_controls : {};
  const entries = Object.entries(controls).filter(([, v]) => v != null && Number(v) !== 0);
  const hasList = entries.length > 0;

  const tooltipContent = hasList ? (
    <Box sx={{ fontFamily: t.fontFamily }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${t.colors.border}`, backgroundColor: t.colors.borderLight }}>
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: t.colors.text }}>Failed compliance controls</Typography>
        <Typography sx={{ fontSize: "0.75rem", color: t.colors.textMuted, mt: 0.25 }}>{entries.length} control{entries.length !== 1 ? "s" : ""}</Typography>
      </Box>
      <Box sx={{ maxHeight: 220, overflowY: "auto", py: 1 }}>
        {entries.map(([name, count]) => (
          <Box key={name} sx={{ px: 2, py: 0.75, fontSize: "0.8125rem", color: t.colors.text, borderBottom: `1px solid ${t.colors.borderLight}` }}>
            <Typography component="span" sx={{ fontWeight: 500 }}>{name}</Typography>
            <Typography component="span" sx={{ color: t.colors.textMuted, ml: 0.5 }}>× {Number(count)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  ) : null;

  const chip = <StatusChip status={row.compliance_status} />;

  if (hasList) {
    return (
      <Tooltip
        title={tooltipContent}
        placement="top-start"
        enterDelay={200}
        leaveDelay={0}
        componentsProps={{ tooltip: { sx: complianceTooltipSx } }}
      >
        <Box component="span" sx={{ display: "inline-block", cursor: "default" }}>
          {chip}
        </Box>
      </Tooltip>
    );
  }
  return chip;
}

function StatusChip({ status, label }) {
  const isFailed = (status || "").toLowerCase() === "failed";
  return (
    <Chip
      size="small"
      label={label ?? (isFailed ? "Failed" : "Passed")}
      sx={{
        fontWeight: 600,
        fontSize: "0.6875rem",
        fontFamily: t.fontFamily,
        borderRadius: t.radius.sm,
        height: 20,
        ...(isFailed
          ? { bgcolor: t.colors.destructiveBg, color: t.colors.destructive, border: `1px solid ${t.colors.destructiveBorder}` }
          : { bgcolor: t.colors.successBg, color: t.colors.success }),
      }}
    />
  );
}

const SECURITY_SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
const SECURITY_SEVERITY_COLORS = {
  CRITICAL: t.colors.severity.critical,
  HIGH: t.colors.severity.high,
  MEDIUM: t.colors.severity.medium,
  LOW: t.colors.severity.low,
};

/** Security column: status + count + severity in one clean line */
function SecurityCell({ row }) {
  const isFailed = (row.security_status || "").toLowerCase() === "failed";
  const failedCount = row.security_failed_checks ?? 0;
  const bySeverity = row.security_failed_checks_by_severity || {};
  const hasSeverity = SECURITY_SEVERITY_ORDER.some((s) => (bySeverity[s] ?? 0) > 0);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        fontFamily: t.fontFamily,
        fontSize: "0.75rem",
        lineHeight: 1.4,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          color: isFailed ? t.colors.destructive : t.colors.success,
        }}
      >
        {isFailed ? "Failed" : "Passed"}
      </Typography>
      {isFailed && failedCount > 0 && (
        <>
          <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", fontWeight: 500 }}>
            ({failedCount})
          </Typography>
          {hasSeverity && (
            <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.7rem", mx: 0.25 }}>·</Typography>
          )}
          {hasSeverity &&
            SECURITY_SEVERITY_ORDER.filter((s) => (bySeverity[s] ?? 0) > 0).map((sev, i) => (
              <React.Fragment key={sev}>
                {i > 0 && (
                  <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.7rem", mr: 0.25 }}>,</Typography>
                )}
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    color: SECURITY_SEVERITY_COLORS[sev] || t.colors.text,
                  }}
                >
                  {sev}: {bySeverity[sev]}
                </Typography>
              </React.Fragment>
            ))}
        </>
      )}
    </Box>
  );
}

/**
 * Account detail content: KPIs, service filter, resources table.
 * Used by Summary page (with accountNumber from dropdown) and by /summary/account/:id route.
 * When accounts/onAccountChange/getAccountLabel are provided, header shows only account dropdown (top right).
 */
export default function SummaryAccountDetailContent({
  accountNumber,
  showBackLink = false,
  accounts = [],
  accountsLoading = false,
  accountsError = null,
  onAccountChange,
  getAccountLabel,
}) {
  const history = useHistory();
  const showAccountDropdown = typeof onAccountChange === "function" && Array.isArray(accounts);
  const [details, setDetails] = useState([]);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [detailsPage, setDetailsPage] = useState(1);
  const [detailsPageSize, setDetailsPageSize] = useState(25);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [servicesList, setServicesList] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [sortBy, setSortBy] = useState("arn");
  const [sortOrder, setSortOrder] = useState("asc");

  const fetchDetails = useCallback(async () => {
    if (!accountNumber) return;
    setDetailsLoading(true);
    setDetailsError(null);
    try {
      const params = { page: detailsPage, page_size: detailsPageSize };
      if (selectedService) params.service = selectedService;
      const response = await get(
        `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/details`,
        { params }
      );
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const items = response?.items ?? [];
        setDetails(Array.isArray(items) ? items : []);
        setDetailsTotal(response?.total ?? items.length);
      } else {
        setDetailsError(getApiErrorMessage(response) || "Failed to load details");
      }
    } catch (err) {
      const errData = err?.data ?? err?.response?.data;
      setDetailsError(getApiErrorMessage(errData) || err?.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  }, [accountNumber, detailsPage, detailsPageSize, selectedService]);

  const fetchServices = useCallback(async () => {
    if (!accountNumber) return;
    try {
      const response = await get(
        `${url.ARN_VIEWS_BASE}/accounts/${encodeURIComponent(accountNumber)}/services`
      );
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const list = response?.services ?? response?.items ?? (Array.isArray(response) ? response : []);
        setServicesList(Array.isArray(list) ? list : []);
      } else {
        setServicesList([]);
      }
    } catch {
      setServicesList([]);
    }
  }, [accountNumber]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleChangePage = (_, newPage) => setDetailsPage(newPage + 1);
  const handleChangeRowsPerPage = (e) => {
    setDetailsPageSize(parseInt(e.target.value, 10));
    setDetailsPage(1);
  };

  const filteredAndSortedDetails = useMemo(() => {
    let list = [...details];
    const q = (tableSearch || "").trim().toLowerCase();
    if (q) {
      list = list.filter((row) => {
        const arn = (row.arn || "").toLowerCase();
        const service = (row.service || "").toLowerCase();
        const region = (row.region || "").toLowerCase();
        const modules = Array.isArray(row.modules) ? row.modules.join(" ").toLowerCase() : "";
        return arn.includes(q) || service.includes(q) || region.includes(q) || modules.includes(q);
      });
    }
    list.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === "arn") {
        aVal = (a.arn || "").toLowerCase();
        bVal = (b.arn || "").toLowerCase();
      } else if (sortBy === "modules") {
        aVal = Array.isArray(a.modules) ? a.modules.join(" ") : "";
        bVal = Array.isArray(b.modules) ? b.modules.join(" ") : "";
      } else if (sortBy === "cost_unblended_mtd" || sortBy === "security_failed_checks" || sortBy === "compliance_failed_total") {
        aVal = Number(aVal) ?? 0;
        bVal = Number(bVal) ?? 0;
      } else if (sortBy === "snapshot_date") {
        aVal = a.snapshot_date || "";
        bVal = b.snapshot_date || "";
      } else {
        aVal = (aVal ?? "").toString().toLowerCase();
        bVal = (bVal ?? "").toString().toLowerCase();
      }
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [details, tableSearch, sortBy, sortOrder]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSortBy(columnId);
      setSortOrder("asc");
    }
  };

  const pageKpis = useMemo(() => {
    let securityFailed = 0;
    let complianceFailed = 0;
    let costMtd = 0;
    filteredAndSortedDetails.forEach((row) => {
      securityFailed += Number(row.security_failed_checks) || 0;
      complianceFailed += Number(row.compliance_failed_total) || 0;
      const c = row.cost_unblended_mtd;
      if (c != null && c !== "") costMtd += Number(c) || 0;
    });
    return { securityFailed, complianceFailed, costMtd };
  }, [filteredAndSortedDetails]);

  const renderHeader = () => (
    <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 2 }}>
      {showAccountDropdown ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 280,
              "& .MuiOutlinedInput-root": {
                fontFamily: t.fontFamily,
                fontSize: "0.875rem",
                backgroundColor: t.colors.background,
                borderRadius: t.radius.md,
                "& fieldset": { borderColor: t.colors.border },
                "&:hover fieldset": { borderColor: t.colors.textMuted },
                "&.Mui-focused fieldset": { borderColor: t.colors.accent, borderWidth: "1px" },
              },
            }}
          >
            <InputLabel id="detail-account-label" sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>
              View account
            </InputLabel>
            <Select
              labelId="detail-account-label"
              id="detail-account-select"
              value={accountNumber || ""}
              label="View account"
              onChange={(e) => onAccountChange(e.target.value)}
              sx={{ fontFamily: t.fontFamily, fontWeight: 500, "& .MuiSelect-select": { py: 1.25 } }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1.5,
                    borderRadius: t.radius.md,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    maxHeight: 320,
                    "& .MuiMenuItem-root": { fontFamily: t.fontFamily, fontSize: "0.875rem" },
                  },
                },
              }}
            >
              {accountsLoading ? (
                <MenuItem value="" disabled>Loading…</MenuItem>
              ) : accountsError ? (
                <MenuItem value="" disabled>{accountsError}</MenuItem>
              ) : (
                accounts.map((acc, i) => {
                  const num = acc?.account_number ?? acc?.accountNumber ?? acc?.id ?? "";
                  if (!num) return null;
                  return (
                    <MenuItem key={num || i} value={num}>
                      {typeof getAccountLabel === "function" ? getAccountLabel(acc) : num}
                    </MenuItem>
                  );
                })
              )}
              {!accountsLoading && !accountsError && accounts.length === 0 && (
                <MenuItem value="" disabled>No accounts</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      ) : (
        showBackLink && (
          <Typography
            component="span"
            onClick={() => history.push("/summary")}
            sx={{
              color: t.colors.accent,
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": { color: t.colors.accentHover, textDecoration: "underline" },
            }}
          >
            ← Back to Summary
          </Typography>
        )
      )}
    </Box>
  );

  if (!accountNumber) {
    return (
      <Box sx={{ fontFamily: t.fontFamily }}>
        {renderHeader()}
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
          <Typography sx={{ color: t.colors.textMuted, fontSize: "0.875rem" }}>
            {showAccountDropdown ? "Select an account from the dropdown above to view details." : "Select an account to view details."}
          </Typography>
        </Box>
      </Box>
    );
  }

  const tableCellSx = {
    fontFamily: t.fontFamily,
    fontSize: "0.8125rem",
    borderBottom: `1px solid ${t.colors.border}`,
    py: 1.25,
    px: 2,
  };
  const headerSx = {
    ...tableCellSx,
    backgroundColor: t.colors.borderLight,
    fontWeight: 600,
    color: t.colors.textMuted,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  return (
    <Box sx={{ fontFamily: t.fontFamily }}>
      {renderHeader()}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
        <Box sx={{ position: "relative", borderRadius: t.radius.lg, overflow: "hidden", backgroundColor: t.colors.card, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow }}>
          <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: t.colors.accent }} />
          <Box sx={{ pl: "16px", pr: 2, py: 2 }}>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: t.colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>Total resources</Typography>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>{detailsTotal}</Typography>
          </Box>
        </Box>
        <Box sx={{ position: "relative", borderRadius: t.radius.lg, overflow: "hidden", backgroundColor: t.colors.card, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow }}>
          <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: t.colors.severity.high }} />
          <Box sx={{ pl: "16px", pr: 2, py: 2 }}>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: t.colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>Security failed (page)</Typography>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>{pageKpis.securityFailed}</Typography>
          </Box>
        </Box>
        <Box sx={{ position: "relative", borderRadius: t.radius.lg, overflow: "hidden", backgroundColor: t.colors.card, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow }}>
          <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: t.colors.warning }} />
          <Box sx={{ pl: "16px", pr: 2, py: 2 }}>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: t.colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>Compliance failed (page)</Typography>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>{pageKpis.complianceFailed}</Typography>
          </Box>
        </Box>
        <Box sx={{ position: "relative", borderRadius: t.radius.lg, overflow: "hidden", backgroundColor: t.colors.card, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow }}>
          <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: t.colors.success }} />
          <Box sx={{ pl: "16px", pr: 2, py: 2 }}>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: t.colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>Cost MTD (page)</Typography>
            <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>{formatCost(pageKpis.costMtd)}</Typography>
          </Box>
        </Box>
      </Box>

      {servicesList.length > 0 && (
        <Box sx={{ borderRadius: t.radius.lg, backgroundColor: t.colors.card, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow, p: 2, mb: 3 }}>
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: t.colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5 }}>Filter by service</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Chip label="All" size="small" onClick={() => { setSelectedService(""); setDetailsPage(1); }} sx={{ fontFamily: t.fontFamily, fontWeight: 500, fontSize: "0.75rem", borderRadius: t.radius.sm, bgcolor: !selectedService ? t.colors.accent : t.colors.borderLight, color: !selectedService ? "#fff" : t.colors.text, cursor: "pointer", "&:hover": { bgcolor: !selectedService ? t.colors.accentHover : "#e4e4e7" } }} />
            {servicesList.map((item, i) => {
              const name = item?.service ?? item?.name ?? String(i);
              const total = item?.total ?? 0;
              return (
                <Chip key={i} label={`${name}: ${total}`} size="small" onClick={() => { setSelectedService(selectedService === name ? "" : name); setDetailsPage(1); }} sx={{ fontFamily: t.fontFamily, fontWeight: 500, fontSize: "0.75rem", borderRadius: t.radius.sm, bgcolor: selectedService === name ? t.colors.accent : t.colors.borderLight, color: selectedService === name ? "#fff" : t.colors.text, cursor: "pointer", "&:hover": { bgcolor: selectedService === name ? t.colors.accentHover : "#e4e4e7" } }} />
              );
            })}
          </Box>
        </Box>
      )}

      {detailsError && (
        <Box sx={{ p: 2, mb: 2, borderRadius: t.radius.md, bgcolor: t.colors.destructiveBg, border: `1px solid ${t.colors.destructiveBorder}` }}>
          <Typography sx={{ fontSize: "0.875rem", color: t.colors.destructive, fontFamily: t.fontFamily }}>{detailsError}</Typography>
        </Box>
      )}

      {detailsLoading && (
        <Box sx={{ display: "flex", alignItems: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: t.colors.accent }} />
          <Typography sx={{ ml: 2, color: t.colors.textMuted, fontSize: "0.875rem", fontFamily: t.fontFamily }}>Loading resources...</Typography>
        </Box>
      )}

      {!detailsLoading && !detailsError && (
        <Box sx={{ backgroundColor: t.colors.card, borderRadius: t.radius.lg, border: `1px solid ${t.colors.border}`, boxShadow: t.shadow, overflow: "hidden" }}>
          {/* Search in table header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${t.colors.border}`, display: "flex", alignItems: "center", gap: 1 }}>
            <SearchIcon sx={{ color: t.colors.textMuted, fontSize: 20 }} />
            <InputBase
              placeholder="Search by ARN, service, region, modules..."
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              size="small"
              sx={{
                flex: 1,
                fontSize: "0.8125rem",
                fontFamily: t.fontFamily,
                "& input": { py: 0.5 },
              }}
            />
            {tableSearch && (
              <Typography component="span" sx={{ fontSize: "0.75rem", color: t.colors.textMuted, fontFamily: t.fontFamily }}>
                {filteredAndSortedDetails.length} of {details.length} on this page
              </Typography>
            )}
          </Box>
          <TableContainer sx={{ maxHeight: "50vh" }}>
            <Table stickyHeader size="medium" sx={{ minWidth: 900, fontFamily: t.fontFamily }}>
              <TableHead>
                <TableRow>
                  {DETAIL_TABLE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        ...headerSx,
                        width: col.width,
                        minWidth: col.minWidth,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      onClick={() => handleSort(col.id)}
                    >
                      <Tooltip title={col.desc || ""} placement="top" arrow>
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                          {col.label}
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: t.colors.textMuted, ml: 0.25 }} />
                          {sortBy === col.id && (sortOrder === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ ...tableCellSx, py: 6, textAlign: "center", color: t.colors.textMuted }}>
                      {tableSearch ? "No rows match your search on this page." : "No resources"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedDetails.map((row, i) => (
                    <TableRow key={row.arn ?? i} sx={{ "& td": tableCellSx, "&:hover": { bgcolor: t.colors.background } }}>
                      <TableCell sx={{ fontFamily: t.fontFamily, minWidth: 180, maxWidth: 360 }}>
                        <Tooltip title={row.arn || ""} placement="top-start">
                          <Typography component="span" sx={{ fontSize: "0.8125rem", fontFamily: "monospace", color: t.colors.text, display: "block", whiteSpace: "normal", wordBreak: "break-all" }}>
                            {row.arn || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontFamily: t.fontFamily, color: t.colors.text, fontWeight: 500 }}>{row.service ?? "—"}</TableCell>
                      <TableCell sx={{ fontFamily: t.fontFamily, color: t.colors.textMuted }}>{row.region ?? "—"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {(row.modules || []).map((m, j) => (
                            <Chip key={j} size="small" label={m} sx={{ fontFamily: t.fontFamily, fontSize: "0.6875rem", height: 18, borderRadius: t.radius.sm, bgcolor: t.colors.borderLight, color: t.colors.text }} />
                          ))}
                          {(!row.modules || row.modules.length === 0) && "—"}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SecurityCell row={row} />
                      </TableCell>
                      <TableCell>
                        <ComplianceCell row={row} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: t.fontFamily, fontVariantNumeric: "tabular-nums" }}>{formatCost(row.cost_unblended_mtd)}</TableCell>
                      <TableCell sx={{ fontFamily: t.fontFamily, color: t.colors.textMuted, fontSize: "0.75rem" }}>{formatDate(row.snapshot_date)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Summary-style footer: Showing X–Y of Z, rows per page, Previous/Next */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `1px solid ${t.colors.border}`,
              backgroundColor: t.colors.card,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              borderRadius: "0 0 12px 12px",
            }}
          >
            <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.8125rem", fontWeight: 400, fontFamily: t.fontFamily }}>
              Showing {detailsTotal === 0 ? 0 : (detailsPage - 1) * detailsPageSize + 1}–
              {Math.min(detailsPage * detailsPageSize, detailsTotal)} of {detailsTotal}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", fontFamily: t.fontFamily }}>
                Rows per page
              </Typography>
              <FormControl size="small" sx={{ minWidth: 56 }}>
                <Select
                  value={detailsPageSize}
                  onChange={(e) => {
                    setDetailsPageSize(Number(e.target.value));
                    setDetailsPage(1);
                  }}
                  sx={{
                    fontSize: "0.8125rem",
                    height: 32,
                    fontFamily: t.fontFamily,
                    color: t.colors.text,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.colors.borderLight },
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n} sx={{ fontFamily: t.fontFamily, fontSize: "0.8125rem" }}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                disabled={detailsPage <= 1}
                onClick={() => setDetailsPage((p) => p - 1)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: t.colors.border,
                  color: t.colors.textMuted,
                  minWidth: "auto",
                  px: 1.5,
                  fontFamily: t.fontFamily,
                  "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                  "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={detailsPage * detailsPageSize >= detailsTotal}
                onClick={() => setDetailsPage((p) => p + 1)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8125rem",
                  borderColor: t.colors.border,
                  color: t.colors.textMuted,
                  minWidth: "auto",
                  px: 1.5,
                  fontFamily: t.fontFamily,
                  "&:hover": { borderColor: t.colors.borderLight, backgroundColor: t.colors.background },
                  "&.Mui-disabled": { borderColor: t.colors.borderLight, color: t.colors.textMutedLight },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
